import type { QueryClient } from '@tanstack/react-query';

const EPOCH_KEY = 'kazna:auth-epoch';
const CHANNEL = 'kazna:auth';
const listeners = new Set<() => void>();
const cacheListeners = new Set<() => void>();
let channel: BroadcastChannel | undefined;
let listening = false;
let notifiedEpoch = '';

export class AuthCoordinationError extends Error {
  readonly code = 'AUTH_COORDINATION_UNSUPPORTED';
  constructor() {
    super('Безопасный вход недоступен в этом браузере. Откройте Казну в актуальной версии Safari или Chrome по HTTPS.');
  }
}

// Never replace this with a per-tab mutex: Set-Cookie happens in the browser,
// before JavaScript can reject a stale response. Work must include body drain.
export async function withCookieMutation<T>(work: () => Promise<T>): Promise<T> {
  if (!navigator.locks?.request) throw new AuthCoordinationError();
  return navigator.locks.request('kazna:auth-cookie', work);
}

export function captureAuthEpoch(): string {
  startListening();
  return notifiedEpoch;
}

function sharedAuthEpoch(): string {
  return localStorage.getItem(EPOCH_KEY) ?? 'ready:initial';
}

export function isCurrentAuthEpoch(epoch: string): boolean {
  // Never adopt another tab's authority just because its notification is late.
  // Dispatch calls this again while holding the cookie lock.
  return captureAuthEpoch() === epoch && sharedAuthEpoch() === epoch;
}

export function isAuthUnknown(): boolean {
  return captureAuthEpoch().startsWith('unknown:') || sharedAuthEpoch().startsWith('unknown:');
}

function notify(): void {
  const next = sharedAuthEpoch();
  if (next === notifiedEpoch) return;
  notifiedEpoch = next;
  // Cache eviction precedes every subscriber, including React bootstrap.
  for (const listener of cacheListeners) listener();
  for (const listener of listeners) listener();
}

function startListening(): void {
  if (listening) return;
  listening = true;
  notifiedEpoch = sharedAuthEpoch();
  window.addEventListener('storage', event => {
    if (event.key === EPOCH_KEY || event.key === null) notify();
  });
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL);
    // Storage is authoritative: an older queued channel event cannot roll back
    // a later change. Messages never carry user IDs, addresses or credentials.
    channel.onmessage = () => notify();
  }
}

function publish(state: 'ready' | 'unknown'): void {
  startListening();
  localStorage.setItem(EPOCH_KEY, `${state}:${crypto.randomUUID()}`);
  localStorage.removeItem('kazna:active-workspace');
  notify();
  channel?.postMessage({ changed: true });
}

export function publishAuthChange(): void { publish('ready'); }
export function markAuthUnknown(): void { publish('unknown'); }

export function subscribeAuthChange(listener: () => void): () => void {
  startListening();
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function bindAuthQueryClient(client: QueryClient): () => void {
  startListening();
  const clear = () => {
    // Cancellation marks promises obsolete synchronously; clearing also removes
    // mutation data and all future user-owned query families by default.
    void client.cancelQueries();
    client.clear();
    localStorage.removeItem('kazna:active-workspace');
  };
  cacheListeners.add(clear);
  return () => { cacheListeners.delete(clear); };
}

// A stale page callback may still hold its QueryClient after unmount. Rotate the
// instance as well as evicting it so that callback cannot invalidate B's cache.
// The provider itself remains unkeyed: public forms retain memory-only retries.
export function createAuthQueryClientStore(createClient: () => QueryClient) {
  startListening();
  let current = createClient();
  const rotate = () => {
    void current.cancelQueries();
    current.clear();
    localStorage.removeItem('kazna:active-workspace');
    current = createClient();
  };
  cacheListeners.add(rotate);
  return {
    getSnapshot: () => current,
    subscribe: subscribeAuthChange,
    dispose: () => { cacheListeners.delete(rotate); current.clear(); },
  };
}
